import {
	type UploadDataType,
	type UploadSource,
	isTiffFileName,
	resolveUploadTypeByExtension,
	uploadSources,
} from "@/components/AddData/uploadTypeResolver";

/** ファイル選択が不正だった理由。エラーメッセージの i18n キー選択に使う */
export const fileSelectionErrorReasons = {
	/** 拡張子が対応外（既存 FILE_TYPE_NOT_SUPPORTED） */
	unsupportedType: "unsupportedType",
	/** tiff 以外を複数選択（既存 SELECT_A_SINGLE_FILE）AC-3 */
	multipleNonTiff: "multipleNonTiff",
	/** tiff が 2 件でない、または他形式と混在（新規 SELECT_EXACTLY_TWO_TIFF_FILES）AC-4 */
	tiffCountMismatch: "tiffCountMismatch",
} as const;

export type FileSelectionErrorReason =
	(typeof fileSelectionErrorReasons)[keyof typeof fileSelectionErrorReasons];

/** 不正理由 -> エラーメッセージの i18n キー（3 キーとも既存）。手動経路・Groupware 経路で共通 */
export const FILE_SELECTION_ERROR_KEYS: Readonly<
	Record<FileSelectionErrorReason, string>
> = {
	unsupportedType: "FILE_TYPE_NOT_SUPPORTED",
	multipleNonTiff: "SELECT_A_SINGLE_FILE",
	tiffCountMismatch: "SELECT_EXACTLY_TWO_TIFF_FILES",
};

/** `<input type=file multiple>` の選択結果を、後続フローが分岐できる形に正規化したもの */
export type FileSelectionResolution =
	// 既存経路（AC-33）
	| {
			readonly kind: "single";
			readonly file: File;
			readonly resolvedType: UploadDataType;
	  }
	// 選択順を保持（AC-7）
	| { readonly kind: "tiffPair"; readonly files: readonly [File, File] }
	| { readonly kind: "invalid"; readonly reason: FileSelectionErrorReason };

/** tiff を含まない選択の判定（既存の単一ファイル経路。AC-3 / AC-33） */
const resolveSingle = (
	files: readonly File[],
	source: UploadSource,
): FileSelectionResolution => {
	if (files.length > 1) {
		return {
			kind: "invalid",
			reason: fileSelectionErrorReasons.multipleNonTiff,
		};
	}

	const file = files[0];
	const resolvedType = resolveUploadTypeByExtension(file.name, source);
	if (!resolvedType) {
		return {
			kind: "invalid",
			reason: fileSelectionErrorReasons.unsupportedType,
		};
	}

	return { kind: "single", file, resolvedType };
};

/**
 * ファイル選択結果を「既存形式の単一ファイル」「tiff ペア」「不正」に正規化する。
 *
 * - tiff は 2 件ちょうどのときだけ `tiffPair`（選択順を保持。AC-2 / AC-7）
 * - tiff が 1 件 / 3 件以上 / 他形式と混在は `tiffCountMismatch`（AC-4）
 * - tiff 以外の複数選択は `multipleNonTiff`（AC-3）
 * - 組の規則は経路によらず同一。source は単一ファイルの許可集合を絞るためだけに使う（GI-1 / GI-34）
 *
 * @param files 選択されたファイル（選択順）
 * @param source アップロード元
 */
export const resolveFileSelection = (
	files: readonly File[],
	source: UploadSource = uploadSources.local,
): FileSelectionResolution => {
	if (files.length === 0) {
		return {
			kind: "invalid",
			reason: fileSelectionErrorReasons.unsupportedType,
		};
	}

	const tiffFiles = files.filter((file) => isTiffFileName(file.name));
	if (tiffFiles.length === 0) {
		return resolveSingle(files, source);
	}

	// tiff が混ざる場合は「tiff がちょうど 2 件、かつ他形式の混在なし」以外を認めない
	if (tiffFiles.length !== 2 || files.length !== 2) {
		return {
			kind: "invalid",
			reason: fileSelectionErrorReasons.tiffCountMismatch,
		};
	}

	return { kind: "tiffPair", files: [tiffFiles[0], tiffFiles[1]] };
};
